/**
 * Übersicht / Lohn-Dashboard — offline-fähig.
 *
 * Statt fixem Kalendermonat wird der User-Abrechnungszeitraum benutzt
 * (`billingCycleStartDay`). Bei startDay=1 ist das identisch zum Monat,
 * bei z. B. 15 läuft der Zyklus vom 15. eines Monats zum 14. des nächsten.
 */

import { useMemo, useState } from 'react';
import { useSettleShift, useShiftsInRange } from '../data/shifts';
import { useLocations } from '../data/locations';
import { ViewSwitcher } from '../components/ViewSwitcher';
import { PayoutCard } from '../components/PayoutCard';
import { formatMoney } from '../lib/format';
import {
  billingCycleAt,
  calculatePeriodWage,
  isSettleableNow,
  minijobStatus,
  percentChange,
  shiftBillingCycle,
  summarize,
  summarizeByIsoWeek,
  summarizeByLocation,
  summarizeSettlement,
  summarizeSplit,
  type BillingCycle,
} from '../lib/payroll';
import { useMinijobLimit } from '../data/config';
import { useAuth, useCanManage } from '../store/auth';
import { usePayouts } from '../data/payouts';
import { usePeriod } from '../store/period';

export default function OverviewPage() {
  const me = useAuth((s) => s.user);
  const breakDeduction = me?.breakDeductionEnabled ?? false;
  const payrollOpts = { breakDeduction };

  // Abrechnungszeitraum aus User-Setting ableiten. Der betrachtete Monat kommt
  // aus dem geteilten Zeitraum-Store, damit Liste/Kalender/Übersicht synchron
  // bleiben. Bei startDay=1 ist der Zyklus == Kalendermonat; bei versetztem
  // Start folgt die Übersicht dem Monat um genau einen Zyklus.
  const startDay = me?.billingCycleStartDay ?? 1;
  const month = usePeriod((s) => s.anchorMonth);
  const prevMonth = usePeriod((s) => s.prev);
  const nextMonth = usePeriod((s) => s.next);
  const cycle = useMemo<BillingCycle>(() => {
    const [y, m] = month.split('-').map(Number);
    const day = Math.min(28, Math.max(1, startDay));
    return billingCycleAt(startDay, new Date(Date.UTC(y!, (m ?? 1) - 1, day)));
  }, [month, startDay]);
  const prevCycle = useMemo(
    () => shiftBillingCycle(cycle, startDay, -1),
    [cycle, startDay],
  );

  const currentShifts = useShiftsInRange(cycle.from, cycle.to) ?? [];
  const previousShifts = useShiftsInRange(prevCycle.from, prevCycle.to) ?? [];
  const locations = useLocations() ?? [];

  const locationsById = useMemo(() => {
    const map = new Map(locations.map((l) => [l.id, l]));
    return map;
  }, [locations]);

  const currentSummary = useMemo(
    () => summarize(currentShifts, payrollOpts),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentShifts, breakDeduction],
  );
  const previousSummary = useMemo(
    () => summarize(previousShifts, payrollOpts),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [previousShifts, breakDeduction],
  );
  const currentSplit = useMemo(
    () => summarizeSplit(currentShifts, payrollOpts),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentShifts, breakDeduction],
  );
  // Gesamtprüfung 11.09.2026: mit Pausen-Abzug wie die Kacheln oben — sonst
  // passten „Pro Standort“/„Pro Woche“ nicht zur Gesamtsumme (> 100 %).
  const byLocation = useMemo(
    () => summarizeByLocation(currentShifts, payrollOpts),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentShifts, breakDeduction],
  );
  const byWeek = useMemo(
    () => summarizeByIsoWeek(currentShifts, payrollOpts),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentShifts, breakDeduction],
  );
  const settlement = useMemo(
    () => summarizeSettlement(currentShifts, payrollOpts),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentShifts, breakDeduction],
  );

  // Abrechnung ganzer Zeitraum — Owner ODER Filialleitung (Kapitel 1.3). Da
  // diese Seite nur die EIGENEN Schichten des eingeloggten Users zeigt, kann
  // eine Filialleitung hier trotzdem nur die Schichten an den SELBST
  // verwalteten Standorten abrechnen (spiegelt das Backend-`/settle`, das
  // Nicht-Owner still auf ihre `managedLocationIds` filtert) — z. B. wenn man
  // an einem anderen Standort auch mal selbst als Mitarbeiter aushilft.
  // Gesamtprüfung 11.09.2026: hier stehen nur die EIGENEN Schichten — die
  // rechnet nur der Chef selbst ab (eine Filialleitung nicht, sonst könnte sie
  // sich bezahlte Schichten als offen markieren und doppelt auszahlen lassen;
  // der Server lehnt das ab). Offene Zusatzstunden-Anträge werden erst nach
  // der Entscheidung abgerechnet, Tagesgeld-Entnahmen nicht aufgehoben.
  const isOwner = useCanManage();
  const canSettle = isOwner;
  const settle = useSettleShift();
  const [settling, setSettling] = useState(false);
  const openShifts = useMemo(
    () =>
      currentShifts.filter(
        (s) => s.status !== 'cancelled' && !s.settledAt && s.bonusStatus !== 'pending' && isSettleableNow(s),
      ),
    [currentShifts],
  );
  const settledShifts = useMemo(
    () => currentShifts.filter((s) => s.status !== 'cancelled' && !!s.settledAt && !s.dailyPayoutAt),
    [currentShifts],
  );

  // Gesamtprüfung 11.09.2026 (Kapitel 16.3): bestätigte Vorschüsse im
  // Zeitraum werden vom offenen Betrag abgezogen.
  const { data: payouts } = usePayouts();
  const confirmedAdvances = useMemo(() => {
    if (!me) return 0;
    return (payouts ?? [])
      .filter(
        (p) =>
          p.userId === me.id &&
          p.type === 'advance' &&
          p.status === 'confirmed' &&
          p.createdAt.slice(0, 10) >= cycle.from &&
          p.createdAt.slice(0, 10) <= cycle.to,
      )
      // Phase L: nicht entschlüsselbare Beträge (null) zählen nicht mit.
      .reduce((sum, p) => sum + (p.amount ?? 0), 0);
  }, [payouts, me, cycle.from, cycle.to]);

  async function settlePeriod(settled: boolean) {
    if (settling) return;
    const targets = settled ? openShifts : settledShifts;
    if (targets.length === 0) return;
    const msg = settled
      ? `${targets.length} offene Schicht(en) im Zeitraum „${cycle.label}" als abgerechnet markieren?`
      : `Abrechnung für ${targets.length} Schicht(en) im Zeitraum „${cycle.label}" wieder aufheben?`;
    if (!confirm(msg)) return;
    setSettling(true);
    try {
      for (const s of targets) {
        await settle.mutateAsync({ id: s.id, settled });
      }
    } finally {
      setSettling(false);
    }
  }
  const minijobLimit = useMinijobLimit();

  // Bei Festgehalt-Modus: Lohn neu berechnen (Festgehalt + Überstunden-Anteil)
  const wageBreakdown = useMemo(() => {
    if (!me) return null;
    return calculatePeriodWage(currentSummary.totalHours, {
      mode: me.salaryMode,
      hourlyWage: me.hourlyWage,
      monthlySalary: me.monthlySalary,
      monthlyTargetHours: me.monthlyTargetHours,
    });
  }, [currentSummary.totalHours, me]);

  // Endgültiger Lohn-Wert: aus der Lohn-Modell-Berechnung, fällt zurück
  // auf die einfache Summe wenn aus irgendeinem Grund kein User da ist.
  const effectiveWage = wageBreakdown?.totalWage ?? currentSummary.totalWage;
  // Vorperiode im selben Lohn-Modell (sonst verglich die Kachel bei
  // Festgehalt das Gehalt mit der reinen Stundensumme der Vorperiode).
  const previousEffectiveWage = useMemo(() => {
    if (!me) return previousSummary.totalWage;
    return calculatePeriodWage(previousSummary.totalHours, {
      mode: me.salaryMode,
      hourlyWage: me.hourlyWage,
      monthlySalary: me.monthlySalary,
      monthlyTargetHours: me.monthlyTargetHours,
    }).totalWage;
  }, [previousSummary.totalHours, previousSummary.totalWage, me]);

  // Gesamtprüfung 11.09.2026 (Kapitel 20.5): die Minijob-Grenze gilt je
  // KALENDERMONAT, nicht je Abrechnungszeitraum (bei Start z. B. am 15.).
  const calendarMonthFrom = `${month}-01`;
  const calendarMonthTo = useMemo(() => {
    const [y, m] = month.split('-').map(Number);
    return new Date(Date.UTC(y!, m!, 0)).toISOString().slice(0, 10);
  }, [month]);
  const calendarMonthShifts = useShiftsInRange(calendarMonthFrom, calendarMonthTo) ?? [];
  const calendarMonthWage = useMemo(() => {
    const sum = summarize(calendarMonthShifts, payrollOpts);
    if (!me) return sum.totalWage;
    return calculatePeriodWage(sum.totalHours, {
      mode: me.salaryMode,
      hourlyWage: me.hourlyWage,
      monthlySalary: me.monthlySalary,
      monthlyTargetHours: me.monthlyTargetHours,
    }).totalWage;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calendarMonthShifts, breakDeduction, me]);
  const minijob = useMemo(
    () => minijobStatus(calendarMonthWage, minijobLimit),
    [calendarMonthWage, minijobLimit],
  );

  // Vorschlag für die Auszahlungs-Bestätigung: erarbeiteter Lohn (nur
  // erledigte Schichten) abzüglich Tagesgeld-Entnahmen und bestätigter
  // Vorschüsse — nicht der Plan-Lohn inkl. künftiger Schichten.
  const suggestedPayout = useMemo(() => {
    const dailyPaid = currentShifts.reduce((sum, s) => sum + (s.dailyPayoutAmount ?? 0), 0);
    const base =
      me?.salaryMode === 'salary'
        ? effectiveWage - dailyPaid
        : summarize(
            currentShifts.filter((s) => s.status === 'done' && !s.dailyPayoutAt),
            payrollOpts,
          ).totalWage;
    return Math.max(0, Math.round((base - confirmedAdvances) * 100) / 100);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentShifts, breakDeduction, me, effectiveWage, confirmedAdvances]);

  const maxWeekHours = byWeek.reduce((a, w) => Math.max(a, w.summary.totalHours), 0);

  return (
    <div className="max-w-2xl lg:max-w-5xl mx-auto space-y-4 pb-12">
      <ViewSwitcher />

      <div className="flex items-center justify-between card p-3">
        <button
          onClick={prevMonth}
          className="btn-ghost px-3 py-1"
          aria-label="Vorheriger Abrechnungszeitraum"
        >
          ‹
        </button>
        <div className="text-center">
          <p className="font-semibold">{cycle.label}</p>
          <p className="text-xs text-slate-500">Vorperiode: {prevCycle.label}</p>
          {startDay !== 1 && (
            <p className="text-[10px] text-slate-500">
              Abrechnungs-Zyklus ab Tag {startDay}
            </p>
          )}
        </div>
        <button
          onClick={nextMonth}
          className="btn-ghost px-3 py-1"
          aria-label="Nächster Abrechnungszeitraum"
        >
          ›
        </button>
      </div>

      <div className="grid grid-cols-3 gap-2 sm:gap-3">
        <Kpi
          label="Stunden"
          value={`${currentSummary.totalHours.toFixed(2).replace('.', ',')} h`}
          delta={percentChange(currentSummary.totalHours, previousSummary.totalHours)}
          sub={
            currentSplit.done.totalHours > 0 || currentSplit.planned.totalHours > 0
              ? `${currentSplit.done.totalHours.toFixed(2).replace('.', ',')} gearb. · ${currentSplit.planned.totalHours.toFixed(2).replace('.', ',')} gepl.`
              : undefined
          }
        />
        <Kpi
          label="Lohn (brutto)"
          value={formatMoney(effectiveWage)}
          delta={percentChange(effectiveWage, previousEffectiveWage)}
          highlight
          sub={
            wageBreakdown?.mode === 'salary'
              ? `Festgehalt ${formatMoney(wageBreakdown.baseWage)}${
                  wageBreakdown.variableWage > 0
                    ? ` + ${formatMoney(wageBreakdown.variableWage)} Überstd.`
                    : ''
                }`
              : currentSplit.done.totalWage > 0 || currentSplit.planned.totalWage > 0
                ? `${formatMoney(currentSplit.done.totalWage)} gearb.`
                : undefined
          }
        />
        <Kpi
          label="Schichten"
          value={String(currentSummary.shiftsCount)}
          delta={percentChange(currentSummary.shiftsCount, previousSummary.shiftsCount)}
          sub={
            currentSplit.done.shiftsCount > 0 || currentSplit.planned.shiftsCount > 0
              ? `${currentSplit.done.shiftsCount}/${currentSplit.planned.shiftsCount} erl./gepl.`
              : undefined
          }
        />
      </div>

      {me?.employmentType === 'minijob' ? (
        <MinijobBar status={minijob} />
      ) : (
        me != null && <EmploymentTypeBadge type={me.employmentType} />
      )}

      <SettlementCard
        settlement={settlement}
        advances={confirmedAdvances}
        canSettle={canSettle}
        openCount={openShifts.length}
        settledCount={settledShifts.length}
        busy={settling}
        onSettleAll={() => settlePeriod(true)}
        onUnsettleAll={() => settlePeriod(false)}
      />

      <PayoutCard cycle={cycle} suggestedAmount={suggestedPayout} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <section className="card p-4">
        <h3 className="text-sm uppercase tracking-wider text-slate-400 mb-3">Pro Standort</h3>
        {byLocation.size === 0 ? (
          <p className="text-sm text-slate-500">Keine Schichten in diesem Monat.</p>
        ) : (
          <ul className="space-y-2">
            {Array.from(byLocation.entries()).map(([locId, sum]) => {
              const loc = locationsById.get(locId);
              const share =
                currentSummary.totalHours > 0
                  ? (sum.totalHours / currentSummary.totalHours) * 100
                  : 0;
              return (
                <li key={locId} className="flex items-center gap-3">
                  <span
                    className="w-3 h-10 rounded shrink-0"
                    style={{ backgroundColor: loc?.color ?? '#475569' }}
                    aria-hidden
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="font-medium truncate">{loc?.name ?? '?'}</span>
                      <span className="text-xs text-slate-400">
                        {share.toFixed(0)} %
                      </span>
                    </div>
                    <div className="text-sm text-slate-300 flex justify-between">
                      <span>
                        {sum.shiftsCount} Schichten ·{' '}
                        {sum.totalHours.toFixed(2).replace('.', ',')} h
                      </span>
                      <span className="text-emerald-400">{formatMoney(sum.totalWage)}</span>
                    </div>
                    <div className="mt-1 h-1 bg-slate-700 rounded">
                      <div
                        className="h-1 rounded"
                        style={{
                          width: `${share}%`,
                          backgroundColor: loc?.color ?? '#475569',
                        }}
                      />
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <section className="card p-4">
        <h3 className="text-sm uppercase tracking-wider text-slate-400 mb-3">
          Pro Kalenderwoche
        </h3>
        {byWeek.length === 0 ? (
          <p className="text-sm text-slate-500">Keine Schichten in diesem Monat.</p>
        ) : (
          <ul className="space-y-2">
            {byWeek.map((entry) => {
              const pct = maxWeekHours > 0 ? (entry.summary.totalHours / maxWeekHours) * 100 : 0;
              return (
                <li key={entry.week} className="flex items-center gap-2 sm:gap-3">
                  <span className="text-[11px] sm:text-xs text-slate-400 w-10 sm:w-12 shrink-0">
                    KW{entry.week}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="h-2 bg-slate-700 rounded">
                      <div className="h-2 bg-brand rounded" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                  <span className="text-[11px] sm:text-xs text-slate-300 text-right shrink-0">
                    <span className="hidden sm:inline">
                      {entry.summary.totalHours.toFixed(2).replace('.', ',')} h ·{' '}
                    </span>
                    <span className="text-emerald-400">
                      {formatMoney(entry.summary.totalWage)}
                    </span>
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </section>
      </div>

      <p className="text-xs text-slate-500 text-center">
        Brutto = Netto (Minijob, keine Steuerabzüge). Abgesagte Schichten sind nicht enthalten.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Abrechnungs-Status (offen vs. abgerechnet)
// ---------------------------------------------------------------------------

function SettlementCard({
  settlement,
  advances,
  canSettle,
  openCount,
  settledCount,
  busy,
  onSettleAll,
  onUnsettleAll,
}: {
  settlement: ReturnType<typeof summarizeSettlement>;
  /** Bestätigte Vorschüsse im Zeitraum (werden vom offenen Betrag abgezogen). */
  advances: number;
  canSettle: boolean;
  openCount: number;
  settledCount: number;
  busy: boolean;
  onSettleAll: () => void;
  onUnsettleAll: () => void;
}) {
  const { settled, open } = settlement;
  if (settled.shiftsCount === 0 && open.shiftsCount === 0) return null;
  const totalWage = settled.totalWage + open.totalWage;
  const settledPct = totalWage > 0 ? (settled.totalWage / totalWage) * 100 : 0;
  return (
    <section className="card p-3 sm:p-4">
      <div className="flex items-baseline justify-between gap-2 mb-2">
        <h3 className="text-sm uppercase tracking-wider text-slate-400">Abrechnung</h3>
        <span className="text-xs text-slate-500">
          {settledPct.toFixed(0)} % abgerechnet
        </span>
      </div>
      <div className="h-2.5 bg-slate-700 rounded overflow-hidden mb-3">
        <div
          className="h-full bg-emerald-500 transition-all"
          style={{ width: `${settledPct}%` }}
        />
      </div>
      <div className="grid grid-cols-2 gap-2 sm:gap-3">
        <div className="bg-slate-900/40 rounded p-2 sm:p-3">
          <p className="text-[11px] sm:text-xs text-slate-400">Abgerechnet</p>
          <p className="text-base sm:text-lg font-semibold text-emerald-400">
            {formatMoney(settled.totalWage)}
          </p>
          <p className="text-[10px] sm:text-xs text-slate-500">
            {settled.shiftsCount} Schichten ·{' '}
            {settled.totalHours.toFixed(2).replace('.', ',')} h
          </p>
        </div>
        <div className="bg-slate-900/40 rounded p-2 sm:p-3">
          <p className="text-[11px] sm:text-xs text-slate-400">Offen</p>
          <p className="text-base sm:text-lg font-semibold text-amber-300">
            {formatMoney(Math.max(0, open.totalWage - advances))}
          </p>
          <p className="text-[10px] sm:text-xs text-slate-500">
            {open.shiftsCount} Schichten ·{' '}
            {open.totalHours.toFixed(2).replace('.', ',')} h
          </p>
          {advances > 0 && (
            <p className="text-[10px] sm:text-xs text-slate-500">
              nach Abzug von {formatMoney(advances)} Vorschuss
            </p>
          )}
        </div>
      </div>

      {canSettle && (openCount > 0 || settledCount > 0) && (
        <div className="flex gap-2 mt-3">
          {openCount > 0 && (
            <button
              onClick={onSettleAll}
              disabled={busy}
              className="flex-1 bg-emerald-600/80 hover:bg-emerald-600 transition rounded py-2 text-sm font-medium disabled:opacity-50"
            >
              {settledCount === 0 ? 'Zeitraum abrechnen' : `${openCount} offene abrechnen`}
            </button>
          )}
          {settledCount > 0 && openCount === 0 && (
            <button
              onClick={onUnsettleAll}
              disabled={busy}
              className="flex-1 bg-slate-700 hover:bg-slate-600 transition rounded py-2 text-sm font-medium disabled:opacity-50"
            >
              Abrechnung aufheben
            </button>
          )}
        </div>
      )}
    </section>
  );
}

function Kpi({
  label,
  value,
  delta,
  highlight,
  sub,
}: {
  label: string;
  value: string;
  delta: number | null;
  highlight?: boolean;
  /** Optionale Zusatzinfo, z. B. "30,00 gearb. · 10,00 gepl." */
  sub?: string;
}) {
  const deltaText =
    delta == null ? '' : `${delta >= 0 ? '+' : ''}${delta.toFixed(0)} %`;
  const deltaColor =
    delta == null
      ? 'text-slate-500'
      : delta > 0
        ? 'text-emerald-400'
        : delta < 0
          ? 'text-amber-400'
          : 'text-slate-400';

  return (
    <div className="card p-2 sm:p-3 min-w-0">
      <p className="text-[11px] sm:text-xs text-slate-400 truncate">{label}</p>
      <p
        className={`text-base sm:text-lg font-semibold mt-0.5 sm:mt-1 truncate ${
          highlight ? 'text-emerald-400' : ''
        }`}
      >
        {value}
      </p>
      {sub && (
        <p className="text-[10px] sm:text-xs mt-0.5 truncate text-slate-400">{sub}</p>
      )}
      <p className={`text-[10px] sm:text-xs mt-0.5 sm:mt-1 truncate ${deltaColor}`}>
        {deltaText || 'Vormonat: 0'}
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Minijob-Grenzen-Balken
//
// Zeigt einen Fortschrittsbalken vom aktuellen Brutto-Lohn relativ zur
// firmenspezifischen Minijob-Grenze und färbt sich abhängig vom Status (ok/caution/warning/
// danger/exceeded). Wenn der Lohn 0 € ist, blenden wir nur den Hinweistext
// dezent ein — kein Spam auf leeren Monaten.
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Beschäftigungsart-Badge (Phase P, Kapitel 20.5, Entscheidung #26)
//
// Für alle Nicht-Minijob-Beschäftigungsarten: nur ein dezentes Label statt
// des Fortschrittsbalkens (der ergibt bei Midijob/Teilzeit/Vollzeit keinen
// Sinn — die Minijob-Grenze betrifft ausschließlich echte Minijobs).
// ---------------------------------------------------------------------------

const EMPLOYMENT_TYPE_BADGE_LABEL: Record<'midijob' | 'parttime' | 'fulltime', string> = {
  midijob: 'Midijob',
  parttime: 'Teilzeit',
  fulltime: 'Vollzeit',
};

function EmploymentTypeBadge({ type }: { type: 'minijob' | 'midijob' | 'parttime' | 'fulltime' }) {
  if (type === 'minijob') return null;
  return (
    <div className="card p-3 flex items-center justify-between">
      <p className="text-xs uppercase tracking-wider text-slate-400">Beschäftigungsart</p>
      <span className="text-xs rounded px-2 py-1 bg-slate-700 text-slate-300">
        {EMPLOYMENT_TYPE_BADGE_LABEL[type]}
      </span>
    </div>
  );
}

function MinijobBar({ status }: { status: ReturnType<typeof minijobStatus> }) {
  const palette = {
    ok:       { bar: 'bg-emerald-500', text: 'text-emerald-400', ring: 'border-emerald-500/30' },
    caution:  { bar: 'bg-yellow-500',  text: 'text-yellow-400',  ring: 'border-yellow-500/30' },
    warning:  { bar: 'bg-orange-500',  text: 'text-orange-400',  ring: 'border-orange-500/30' },
    danger:   { bar: 'bg-red-500',     text: 'text-red-400',     ring: 'border-red-500/40' },
    exceeded: { bar: 'bg-red-600',     text: 'text-red-300',     ring: 'border-red-500/60' },
  }[status.level];

  const widthPct = Math.min(100, status.percent);

  return (
    <section
      className={`card p-3 sm:p-4 border ${palette.ring}`}
      aria-label="Minijob-Grenze"
    >
      <div className="flex items-baseline justify-between gap-2 mb-1.5">
        <p className="text-xs uppercase tracking-wider text-slate-400">
          Minijob-Grenze ({formatMoney(status.limit)})
        </p>
        <p className={`text-xs font-medium ${palette.text}`}>
          {status.percent.toFixed(1).replace('.', ',')} %
        </p>
      </div>

      <div className="h-2.5 sm:h-3 bg-slate-700 rounded relative overflow-hidden">
        <div
          className={`h-full ${palette.bar} transition-all`}
          style={{ width: `${widthPct}%` }}
        />
        {status.level === 'exceeded' && (
          <div className="absolute inset-y-0 right-0 w-1 bg-red-300/80" />
        )}
      </div>

      <p className={`text-xs sm:text-sm mt-2 ${palette.text}`}>
        {status.label}
        {status.level !== 'exceeded' && status.remaining > 0 && (
          <span className="text-slate-400">
            {' · '}noch {formatMoney(status.remaining)} bis zur Grenze
          </span>
        )}
        {status.level === 'exceeded' && (
          <span className="text-red-300">
            {' · '}{formatMoney(-status.remaining)} über der Grenze!
          </span>
        )}
      </p>

      {status.level === 'exceeded' && (
        <p className="text-[11px] text-slate-500 mt-1">
          Achtung: Bei Überschreitung droht der Wegfall der Minijob-Steuerfreiheit.
          Sprich mit deinem Arbeitgeber, ob die Schicht in den Folgemonat verschoben werden kann.
        </p>
      )}
    </section>
  );
}
